import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ProductSelectorItem } from '../../Types/ProductPageModel';
import LoadingSpinner from '../LoadingSpinner';
import clsx from 'clsx';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { translate } from '../../Services/translation';
import Icon from '../Icon';
import { Navigation } from 'swiper';
import useReady from '../../Hooks/useReady';

const MAX_ITEMS_UNTIL_SLIDER = 6;

interface ProductSelectorProps {
  items: ProductSelectorItem[];
  onVariantSelected: (id: string) => void;
  loadingProductId?: string;
  selectedProductId?: string;
}

const ProductSelector = ({
  selectedProductId,
  items,
  onVariantSelected,
  loadingProductId,
}: ProductSelectorProps) => {
  const ready = useReady();
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [loadingSpinnerTimeoutId, setLoadingSpinnerTimeoutId] = useState<
    NodeJS.Timeout | undefined
  >();
  const swiperRef = useRef<SwiperRef | null>(null);

  useEffect(() => {
    clearTimeout(loadingSpinnerTimeoutId);
    if (typeof loadingProductId !== 'undefined') {
      const timeout = setTimeout(() => setShowLoadingSpinner(true), 150);
      setLoadingSpinnerTimeoutId(timeout);
    } else {
      setShowLoadingSpinner(false);
    }
  }, [loadingProductId]);

  const showSlider = useMemo(
    () => items.length > MAX_ITEMS_UNTIL_SLIDER,
    [items.length]
  );

  const currentProductIndex = useMemo(
    () => items.findIndex((i) => i.id == selectedProductId),
    [items, selectedProductId]
  );

  useEffect(() => {
    if (swiperRef.current?.swiper) {
      swiperRef.current.swiper.slideTo(currentProductIndex);
    }
  }, [currentProductIndex]);

  const prevBtnRef = useRef<HTMLButtonElement | null>(null);
  const nextBtnRef = useRef<HTMLButtonElement | null>(null);

  return (
    <div className="w-full overflow-hidden">
      <h5 className="mb-2 flex items-end justify-between">
        {translate('product-page.more-variants')}
        {showSlider && (
          <div className="flex gap-2">
            <button
              className="group h-6 w-6 rounded-full bg-gray-100 disabled:bg-gray-50"
              ref={prevBtnRef}
              disabled={!ready}
            >
              <Icon
                name="chevron-left"
                className="pointer-events-none text-gray-700 group-disabled:opacity-50"
              />
            </button>
            <button
              className="group h-6 w-6 rounded-full bg-gray-100 disabled:bg-gray-50"
              ref={nextBtnRef}
              disabled={!ready}
            >
              <Icon
                name="chevron-right"
                className="pointer-events-none text-gray-700 group-disabled:opacity-50"
              />
            </button>
          </div>
        )}
      </h5>
      {(!showSlider || !ready) && (
        <div
          className="grid gap-2 overflow-hidden"
          style={{
            gridTemplateColumns: `repeat(${MAX_ITEMS_UNTIL_SLIDER}, minmax(0, 1fr))`,
          }}
        >
          {items.slice(0, MAX_ITEMS_UNTIL_SLIDER).map((item) => (
            <ProductSelectorItemElement
              key={`${item.id}-product-selector`}
              item={item}
              selectedProductId={selectedProductId}
              loadingProductId={loadingProductId}
              showLoadingSpinner={showLoadingSpinner}
              onVariantSelected={onVariantSelected}
              skeleton={
                showSlider &&
                currentProductIndex > MAX_ITEMS_UNTIL_SLIDER - 1
              }
            />
          ))}
        </div>
      )}
      {showSlider && ready && (
        <Swiper
          slidesPerView={MAX_ITEMS_UNTIL_SLIDER}
          slidesPerGroup={MAX_ITEMS_UNTIL_SLIDER}
          spaceBetween={8}
          modules={[Navigation]}
          navigation={{
            prevEl: prevBtnRef?.current,
            nextEl: nextBtnRef?.current,
          }}
          initialSlide={currentProductIndex}
          ref={swiperRef}
          threshold={5}
        >
          {items.map((item) => (
            <SwiperSlide key={`${item.id}-product-selector`}>
              <ProductSelectorItemElement
                item={item}
                selectedProductId={selectedProductId}
                loadingProductId={loadingProductId}
                showLoadingSpinner={showLoadingSpinner}
                onVariantSelected={onVariantSelected}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </div>
  );
};

interface ProductSelectorItemElementProps {
  selectedProductId?: string;
  item: ProductSelectorItem;
  onVariantSelected: (id: string) => void;
  showLoadingSpinner: boolean;
  loadingProductId?: string;
  className?: string;
  skeleton?: boolean;
}

const ProductSelectorItemElement = ({
  selectedProductId,
  item,
  onVariantSelected,
  showLoadingSpinner,
  loadingProductId,
  className,
  skeleton = false,
}: ProductSelectorItemElementProps) => (
  <a
    className={clsx(
      'group relative my-2 block overflow-hidden rounded border bg-gray-100 transition-all duration-500 will-change-transform aspect-[3/4]',
      className,
      selectedProductId == item.id
        ? 'border-gray-700'
        : 'border-transparent hover:[&_>img]:scale-110',
      (typeof loadingProductId !== 'undefined' ||
        selectedProductId == item.id) &&
        'disabled pointer-events-none'
    )}
    onClick={(e) => {
      e.preventDefault();
      onVariantSelected(item.id);
    }}
    href={item.url}
  >
    {!skeleton && (
      <img
        src={item.imageUrl}
        className={clsx(
          'h-full w-full cursor-pointer rounded object-cover transition-transform aspect-[3/4] group-[.disabled]:pointer-events-none',
          showLoadingSpinner && 'group-[.disabled]:opacity-50'
        )}
        alt={item.name}
        loading="lazy"
      />
    )}
    {loadingProductId == item.id && showLoadingSpinner && (
      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <LoadingSpinner className="h-6 w-6 text-primary-200" />
      </span>
    )}
  </a>
);

export default ProductSelector;
